/**
 * Where the star view puts things (ADR-0040, slice 04).
 *
 * Pure geometry, so it is unit-testable and the SVG stays dumb: the API's
 * verdict decides the ring, the position in the list decides the angle. Two
 * concentric rings — the suggested matches inside, every other suggestion
 * outside.
 */
import { MATCH_TIERS, type MatchTier } from './match-tier'

/** The star never shows more than this many candidates (SC-007). */
export const STAR_MAX_CANDIDATES = 7

/** Side of the square viewport the layout is calibrated for. */
export const STAR_SIZE = 440

/**
 * Radius of the centre node, and of a candidate node, in viewport units. The
 * layout owns them because the rings have to clear both: the innermost ring
 * must not sit on the centre photo, and the outermost node with its score
 * label must stay inside the viewport.
 */
export const CENTER_RADIUS = 46
export const NODE_RADIUS = 30

/** Height the score label needs under the outermost node. */
export const LABEL_HEIGHT = 16

/**
 * Ring radius per tier, as a share of the viewport's half-size. Far enough
 * apart that a node on one ring never touches a node on the other, and each
 * ring carries its pill on its top edge.
 */
const RING_SHARE: Record<MatchTier, number> = {
  suggested: 0.5,
  other: 0.79,
}

/**
 * The angular slice at the top of every ring kept free of nodes: the ring's
 * pill sits there. Seven candidates fit in what is left, still without touching.
 */
const LABEL_GAP = (44 * Math.PI) / 180

/** What the layout needs of a candidate; the view adds the rest. */
export interface StarLayoutInput {
  faceId: string
  /** The API's verdict — a suggested match sits on the inner ring. */
  suggestedMatch: boolean
}

/** One placed candidate, in viewport coordinates. */
export interface StarNode<T extends StarLayoutInput = StarLayoutInput> {
  candidate: T
  tier: MatchTier
  /** Distance from the centre, in viewport units. */
  radius: number
  x: number
  y: number
}

/** One ring to draw, with the edge its pill goes on. */
export interface StarRing {
  tier: MatchTier
  radius: number
  /** Where the pill sits: on the ring's top edge. */
  labelX: number
  labelY: number
}

export interface StarLayout<T extends StarLayoutInput = StarLayoutInput> {
  size: number
  center: number
  nodes: StarNode<T>[]
  rings: StarRing[]
}

export interface StarLayoutOptions {
  /** Side of the square viewport. */
  size?: number
  /** Cap on the candidates placed; the rest is not drawn. */
  maxCandidates?: number
}

/** Ring radius of *tier* inside a viewport of *size*. */
export function ringRadius(tier: MatchTier, size: number): number {
  return (RING_SHARE[tier] * size) / 2
}

/**
 * Place up to *maxCandidates* candidates around the centre.
 *
 * The angle comes from the index in the input — suggested matches first, then
 * by similarity, as the API orders them — and is spread over the circle minus
 * the pill gap at the top, so seven nodes never overlap each other and never
 * cover a pill; the radius comes from the tier.
 */
export function starLayout<T extends StarLayoutInput>(
  candidates: T[],
  { size = STAR_SIZE, maxCandidates = STAR_MAX_CANDIDATES }: StarLayoutOptions = {}
): StarLayout<T> {
  const placed = candidates.slice(0, Math.max(0, maxCandidates))
  const center = size / 2
  const step = placed.length > 0 ? (2 * Math.PI - LABEL_GAP) / placed.length : 0

  const nodes = placed.map((candidate, index) => {
    const tier: MatchTier = candidate.suggestedMatch ? 'suggested' : 'other'
    const radius = ringRadius(tier, size)
    const angle = -Math.PI / 2 + LABEL_GAP / 2 + (index + 0.5) * step
    return {
      candidate,
      tier,
      radius,
      x: center + radius * Math.cos(angle),
      y: center + radius * Math.sin(angle),
    }
  })

  // Only the tiers actually on screen get a ring, the suggested matches first.
  const shown = new Set(nodes.map(node => node.tier))
  const rings = MATCH_TIERS.filter(tier => shown.has(tier)).map(tier => {
    const radius = ringRadius(tier, size)
    return { tier, radius, labelX: center, labelY: center - radius }
  })

  return { size, center, nodes, rings }
}
